import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
    Card,
    Button,
    Checkbox,
    message,
    Upload,
    Space,
    Modal,
    Row,
    Col,
    Typography,
    theme,
    Form,
    Select,
    Input,
    Divider,
    Spin,
    Progress,
    List,
    Image,
    Tooltip
} from 'antd';
import {
    InboxOutlined,
    FileOutlined,
    FolderOpenOutlined,
    EyeOutlined,
    LoadingOutlined,
    DownloadOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useGetAllClients, useGetAllFinancialYears, useGetAllFolders, useGetAllPrimaryClientsDocs } from '../utils/queryUtils';
import useFetch from '../_fetch';
import FileItem from '../components/FilesItem';


const { Dragger } = Upload;
const { Text } = Typography;
const layout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
};

function DrivePage() {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [showSelectionBar, setShowSelectionBar] = useState(false);
    const [moveModalVisible, setMoveModalVisible] = useState(false);
    const [previewModalVisible, setPreviewModalVisible] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [fy_id, setFy_id] = useState(null);
    const [uploadingFiles, setUploadingFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState({});

    const { _id } = useSelector((st) => st.user);
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [newFolder, setNewFolder] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isDraggingOver, setIsDraggingOver] = useState(false);

    const { name: selectedCompany, _id: selectedCompanyId } = useSelector(st => st.selected.company || {});
    const all_clients_from_cache = useGetAllClients(selectedCompany);
    const all_clients = all_clients_from_cache?.data?.data;
    const {
        token: { colorPrimary },
    } = theme.useToken();
    const [selectedFY, setSelectedFY] = useState(null);

    const dragCounterRef = useRef(0);
    const filesRef = useRef([]);
    const _fetch = useFetch();
    const [form] = Form.useForm();

    const selectedClient = useSelector((st) => st.selected?.primaryClient || {});
    const data = useGetAllFinancialYears(selectedEntity);
    const all_fys = data?.data;
    const foldersDataCache = useGetAllFolders(fy_id);
    const all_folders = foldersDataCache?.data;
    const useGetAllDocuments = useGetAllPrimaryClientsDocs(selectedClient._id ? selectedClient._id : '');
    const all_docs = useGetAllDocuments?.data?.data;
    const [files, setFiles] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);

    const { Option } = Select;

    useEffect(() => {
        if (all_docs) {
            setFiles(all_docs);
            setInitialLoading(false);
        }
    }, [all_docs]);

    useEffect(() => {
        if (selectedClient._id) {
            setInitialLoading(true);
            useGetAllDocuments.refetch().then(() => {
                setInitialLoading(false);
            });
        }
    }, [selectedClient._id]);

    const updateUploadProgress = (fileId, progress) => {
        setUploadProgress(prev => ({
            ...prev,
            [fileId]: progress
        }));
    };

    const addUploadingFile = (file) => {
        const uploadingFile = {
            uid: file.uid || Date.now() + Math.random(),
            name: file.name,
            size: file.size,
            type: file.type,
            status: 'uploading'
        };
        setUploadingFiles(prev => [...prev, uploadingFile]);
        return uploadingFile.uid;
    };

    const removeUploadingFile = (fileId) => {
        setUploadingFiles(prev => prev.filter(f => f.uid !== fileId));
        setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[fileId];
            return newProgress;
        });
    };

    const upload = async (file, url, document_id, resolve, reject) => {
        try {
            const fileId = addUploadingFile(file);

            let options;
            let contentType = file.type;
            options = {
                method: "PUT",
                headers: {
                    "Content-Type": contentType,
                },
                body: file,
            };

            // Simulate upload progress (replace with actual progress if available)
            let progress = 0;
            const progressInterval = setInterval(() => {
                progress += 10;
                if (progress <= 90) {
                    updateUploadProgress(fileId, progress);
                }
            }, 200);

            // Actual upload logic
            let res = await fetch(url, options);
            if (res.status === 200) {
                try {
                    let parsedUrl = new URL(url);
                    let pathname = parsedUrl.pathname.slice(1);

                    clearInterval(progressInterval);
                    updateUploadProgress(fileId, 100);

                    let uploadedRes = await _fetch(
                        `${process.env.REACT_APP_API_URL}/file`,
                        {
                            method: "POST",
                            body: {
                                document_id,
                                path: pathname,
                                type: contentType,
                            },
                        }
                    );

                    if (uploadedRes.status >= 200 && uploadedRes.status < 300) {
                        uploadedRes = await uploadedRes.json();
                        resolve(fileId);
                        message.success(`${file.name} uploaded successfully.`);

                        // Remove from uploading list
                        removeUploadingFile(fileId);

                        // Refetch documents
                        await useGetAllDocuments.refetch();
                    }
                } catch (e) {
                    console.error(e);
                    removeUploadingFile(fileId);
                    reject(e);
                }
            }
        } catch (e) {
            console.error(e);
            reject(e);
        }
    };

    async function getUploadUrl(file, id) {
        const fileParts = file.name.split(".");
        fileParts.pop();
        let name = fileParts.join(".");
        let ext = file.name.split(".").pop();
        // create a unique name using timestamp + random suffix to avoid collisions
        const uniqueSuffix = `${moment().valueOf()}-${Math.random().toString(36).slice(2, 8)}`;
        name = `${name}-${uniqueSuffix}.${ext}`;
        let res = await _fetch(
            `${process.env.REACT_APP_API_URL}/file/get_upload_url?name=${encodeURIComponent(name)}&document_id=${id}`
        );
        if (res) {
            const json = await res.json();
            // backend returns { response: '<upload-url>' }
            return json?.response || json;
        }
        console.error("url to upload generation failed");
        throw new Error("url to upload generation failed");
    }

    async function getFolder(name) {
        let body = {
            name: name,
            fy_id,
        }
        try {
            let res = await _fetch(`${process.env.REACT_APP_API_URL}/folder`,
                {
                    method: 'POST',
                    body
                }
            );
            let data = await res.json();
            if (data) {
                return data._id
            } else {
                console.error(data)
                message.error(data.response);
            }
        }
        catch (e) {
            console.error(e);
        }
    }

    const handleUpload = useCallback(async (info) => {
        const { file } = info;

        if (file.status === 'uploading') {
            message.loading(`Uploading ${file.name}...`);
            return;
        }

        if (file.status === 'done') {
            return;
        }

        if (file.status === 'error') {
            message.error(`${file.name} upload failed.`);
        }
    }, []);

    const handleFileDrop = async (droppedFiles) => {
        if (selectedClient._id) {
            message.error('You can only upload files for one client at a time.');
            return;
        }

        setLoading(true);
        // make a local copy to avoid shared mutable state being changed during async ops
        const filesToProcess = Array.from(droppedFiles);

        try {
            const body = {
                primary_client: _id,
                status: "confirmed",
                company: selectedCompanyId
            };

            let createdDocumentRes = await _fetch(
                `${process.env.REACT_APP_API_URL}/document`,
                {
                    method: 'POST',
                    body
                }
            );

            let createdDocument = await createdDocumentRes.json();

            if (createdDocument && createdDocument._id) {
                const document_id = createdDocument._id;
                let promises = [];

                for (let i = 0; i < filesToProcess.length; i += 1) {
                    // capture file in local scope
                    const file = filesToProcess[i];
                    promises[i] = new Promise(async function (resolve, reject) {
                        try {
                            let url = await getUploadUrl(file, document_id);
                            console.log('Uploading', file.name, 'to', url);
                            if (url) await upload(file, url, document_id, resolve, reject);
                            else reject(new Error('No upload url'));
                        } catch (err) {
                            console.error('Upload promise error for', file.name, err);
                            reject(err);
                        }
                    });
                }

                await Promise.all(promises);
                message.success(`Successfully uploaded ${filesToProcess.length} file(s)`);
            } else {
                message.error('Failed to create document entry');
            }
        } catch (e) {
            console.error(e);
            message.error('File upload failed');
        } finally {
            setLoading(false);
            // clear files ref if it was used elsewhere
            filesRef.current = [];
        }
    };

    // Full screen drag and drop handlers
    useEffect(() => {
        const handleDragEnter = (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (!selectedClient._id) {
                dragCounterRef.current++;
                setIsDraggingOver(true);
            }
        };

        const handleDragLeave = (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (!selectedClient._id) {
                dragCounterRef.current--;
                if (dragCounterRef.current === 0) {
                    setIsDraggingOver(false);
                }
            }
        };

        const handleDragOver = (e) => {
            e.preventDefault();
            e.stopPropagation();
        };

        const handleDrop = (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (!selectedClient._id) {
                setIsDraggingOver(false);
                dragCounterRef.current = 0;

                const droppedFiles = Array.from(e.dataTransfer.files);
                handleFileDrop(droppedFiles);
            }
        };

        if (!selectedClient._id) {
            document.addEventListener('dragenter', handleDragEnter);
            document.addEventListener('dragleave', handleDragLeave);
            document.addEventListener('dragover', handleDragOver);
            document.addEventListener('drop', handleDrop);

            return () => {
                document.removeEventListener('dragenter', handleDragEnter);
                document.removeEventListener('dragleave', handleDragLeave);
                document.removeEventListener('dragover', handleDragOver);
                document.removeEventListener('drop', handleDrop);
            };
        }
    }, [selectedClient._id, selectedEntity, selectedFY]);

    const uploadProps = {
        name: 'file',
        multiple: true,
        customRequest: ({ file, onSuccess, onError }) => {
            filesRef.current = [file];
            handleFileDrop([file])
                .then(() => onSuccess())
                .catch((error) => onError(error));
        },
        onChange: handleUpload,
        showUploadList: false,
        disabled: !!selectedClient._id,
    };

    const toggleFileSelection = (fileId) => {
        if (!selectedClient._id) return;

        setSelectedFiles(prev =>
            prev.includes(fileId)
                ? prev.filter(id => id !== fileId)
                : [...prev, fileId]
        );

        const newSelectedFiles = selectedFiles.includes(fileId)
            ? selectedFiles.filter(id => id !== fileId)
            : [...selectedFiles, fileId];

        setShowSelectionBar(newSelectedFiles.length > 0);
    };

    const handleSelectAll = (checked) => {
        if (!selectedClient._id) return;

        setSelectAll(checked);
        if (checked) {
            setSelectedFiles(files?.map(file => file._id));
            setShowSelectionBar(true);
        } else {
            setSelectedFiles([]);
            setShowSelectionBar(false);
        }
    };

    const handleMove = () => {
        setMoveModalVisible(true);
    };

    const handleSingleFileMove = (fileId, e) => {
        e.stopPropagation();
        setSelectedFiles([fileId]);
        setMoveModalVisible(true);
    };


    // Enhanced move functionality
    const confirmMove = async () => {
        if (!selectedEntity || !selectedFY) {
            message.error('Please select both entity and financial year');
            return;
        }
        if (!selectedFiles.length) {
            message.error('Please select at least one file');
            return;
        }
        if (!form.getFieldValue('new_folder') && !form.getFieldValue('folder_id')) {
            message.error('Please select a folder or create a new one first');
            return;
        }

        setLoading(true);

        try {
            let folderId;

            if (newFolder && form.getFieldValue('new_folder')) {
                folderId = await getFolder(form.getFieldValue('new_folder'));
                if (!folderId) {
                    message.error('Failed to create folder');
                    return;
                }
            } else {
                folderId = form.getFieldValue('folder_id');
            }

            for (const fileId of selectedFiles) {
                const docId = files.find(doc => doc._id === fileId)?.document_id;
                console.log(docId);
                const moveData = {
                    entity_id: selectedEntity._id,
                    fy_id: selectedFY._id || selectedFY,
                    folder_id: folderId,
                    primaryClient: selectedClient._id,
                };

                const moveRes = await _fetch(
                    `${process.env.REACT_APP_API_URL}/document/${docId}`,
                    {
                        method: 'PATCH',
                        body: moveData,
                    }
                );

                if (!moveRes.ok) {
                    message.error(`Failed to move document: ${fileId}`);
                }
            }

            await useGetAllDocuments.refetch();

            setSelectedFiles([]);
            setSelectAll(false);
            setShowSelectionBar(false);
            setMoveModalVisible(false);
            setSelectedEntity(null);
            setSelectedFY(null);
            form.resetFields();

            message.success(`Moved ${selectedFiles.length} file(s) successfully`);
        } catch (error) {
            console.error('Move error:', error);
            message.error('Failed to move documents');
        } finally {
            setLoading(false);
        }
    };

    // Enhanced file preview
    const handlePreview = async (file) => {
        setPreviewFile(file);
        setPreviewModalVisible(true);
    };

    const handleDownload = async (file) => {
        window.open(file.path, '_blank', 'noopener,noreferrer');
    }

    const handleEntityChange = (selectedId) => {
        const selectedObject = all_clients.find(client => client._id === selectedId);

        if (selectedObject) {
            setSelectedEntity(selectedObject);
            data?.refetch();
            form.setFieldsValue({ financialYear: undefined });
        }
    };

    const handleFYChange = (value) => {
        console.log(value, 'sshjdhsjds');
        setFy_id(value);
        setSelectedFY(value);
        foldersDataCache?.refetch();
        form.setFieldsValue({ folder_id: undefined });
    };

    const renderPreviewContent = (file) => {
        console.log(file);
        if (!file) return null;

        if (file.type && file.type.includes('image')) {
            return (
                <div style={{ textAlign: 'center' }}>
                    <Image
                        src={file.path}
                        alt={file.name}
                        style={{ maxWidth: '100%', maxHeight: '70vh' }}
                    />
                </div>
            );
        } else if (file.type && file.type.includes('pdf')) {
            return (
                <div style={{ textAlign: 'center', height: '70vh' }}>
                    <iframe
                        src={file.path}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title={file.name}
                    />
                </div>
            );
        } else {
            return (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <FileOutlined style={{ fontSize: '64px' }} />
                    <div style={{ marginTop: '16px' }}>
                        <Text>Preview not available for this file type</Text>
                    </div>
                </div>
            );
        }
    };

    return (
        <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh', position: 'relative' }}>
            {!selectedClient._id && isDraggingOver && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(24, 144, 255, 0.1)',
                        border: `3px dashed ${colorPrimary}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        zIndex: 9999,
                    }}
                >
                    <div style={{ textAlign: 'center', color: colorPrimary }}>
                        <InboxOutlined style={{ fontSize: '64px', marginBottom: '16px' }} />
                        <h2>Drop files anywhere to upload</h2>
                    </div>
                </div>
            )}

            {showSelectionBar && selectedClient._id && (
                <Card
                    style={{
                        marginBottom: 16,
                        backgroundColor: colorPrimary,
                        color: 'white',
                        borderRadius: '8px',
                        position: 'relative',
                    }}
                >
                    <Space size="large" style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Space size="large">
                            <Checkbox
                                checked={selectAll}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                                style={{ color: 'white' }}
                            >
                                {selectAll ? 'Deselect All' : 'Select All'}
                            </Checkbox>

                            <Text style={{ color: 'white' }}>
                                {selectedFiles.length} item(s) selected
                            </Text>
                        </Space>

                        <Button
                            type="primary"
                            style={{ background: 'white', color: colorPrimary, border: 'none' }}
                            onClick={handleMove}
                            disabled={loading}
                        >
                            Move
                        </Button>
                    </Space>
                </Card>
            )}

            {/* Uploading Files Progress */}
            {uploadingFiles.length > 0 && (
                <Card style={{ marginBottom: 16 }}>
                    <Text strong>Uploading Files</Text>
                    <List
                        size="small"
                        dataSource={uploadingFiles}
                        renderItem={(file) => (
                            <List.Item>
                                <div style={{ width: '100%' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <Text>{file.name}</Text>
                                        <Text>{uploadProgress[file.uid] || 0}%</Text>
                                    </div>
                                    <Progress percent={uploadProgress[file.uid] || 0} size="small" />
                                </div>
                            </List.Item>
                        )}
                    />
                </Card>
            )}

            <Row gutter={16} style={{ position: 'relative' }}>
                {!selectedClient._id && (
                    <Col span={6}>
                        <Dragger {...uploadProps} style={{ borderRadius: 8 }}>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">Click or drag files to upload</p>
                        </Dragger>
                    </Col>
                )}

                <Col span={selectedClient?._id ? 24 : 18}>
                    <Card>
                        {initialLoading ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                                <div style={{ marginTop: 16 }}>Loading documents...</div>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-start' }}>
                                <Image.PreviewGroup>
                                    {files?.map(file => {
                                        // Create a wrapper div for selection functionality
                                        return (
                                            <div
                                                key={file._id}
                                                style={{
                                                    position: 'relative',
                                                    cursor: selectedClient._id ? 'pointer' : 'default',
                                                    border: selectedFiles.includes(file._id) ? `2px solid ${colorPrimary}` : 'none',
                                                    borderRadius: '8px',
                                                    padding: selectedFiles.includes(file._id) ? '2px' : '4px',
                                                }}
                                                onClick={() => selectedClient._id && toggleFileSelection(file._id)}
                                            >
                                                {/* Selection checkbox */}
                                                {selectedClient._id && (
                                                    <Checkbox
                                                        checked={selectedFiles.includes(file._id)}
                                                        style={{
                                                            position: 'absolute',
                                                            top: '8px',
                                                            left: '8px',
                                                            zIndex: 10,
                                                            backgroundColor: 'white',
                                                            borderRadius: '2px'
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleFileSelection(file._id);
                                                        }}
                                                    />
                                                )}

                                                {/* Action buttons */}
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '8px',
                                                    right: '8px',
                                                    zIndex: 10,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                    borderRadius: '4px',
                                                    padding: '2px'
                                                }}>
                                                    <Space size="small">
                                                        <Tooltip title="Preview">
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<EyeOutlined />}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handlePreview(file);
                                                                }}
                                                            />
                                                        </Tooltip>

                                                        {selectedClient._id && (
                                                            <Tooltip title="Move document">
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={<FolderOpenOutlined />}
                                                                    onClick={(e) => handleSingleFileMove(file._id, e)}
                                                                />
                                                            </Tooltip>
                                                        )}

                                                        {/* <Tooltip title="Delete">
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<DeleteOutlined />}
                                                                onClick={(e) => handleDeleteFile(file._id, e)}
                                                                danger
                                                            />
                                                        </Tooltip> */}
                                                    </Space>
                                                </div>

                                                {/* FileItem component */}
                                                <FileItem
                                                    file={file}

                                                    variant="drive"
                                                />
                                            </div>
                                        );
                                    })}
                                </Image.PreviewGroup>

                                {files?.length === 0 && !initialLoading && (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#999', width: '100%' }}>
                                        <FileOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                                        <div>No documents found</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>

            <Modal
                title="Move Documents"
                open={moveModalVisible}
                onOk={confirmMove}
                onCancel={() => {
                    setMoveModalVisible(false);
                    setSelectedEntity(null);
                    setSelectedFY(null);
                    setNewFolder(false);
                    form.resetFields();
                    if (!selectedClient._id) {
                        setSelectedFiles([]);
                    }
                }}
                okText="Move"
                width={400}
                confirmLoading={loading}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text>Move {selectedFiles.length} selected file(s) to:</Text>
                </div>

                <Form layout="vertical" {...layout} form={form}>
                    <Form.Item
                        label="Entity"
                        name="client_id"
                        rules={[{ required: true, message: 'Please select an entity' }]}
                    >
                        <Select showSearch optionFilterProp="label" onChange={handleEntityChange}>
                            {all_clients?.map(e => (
                                <Option value={e._id} key={e._id} label={e.title}>{e.title}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => prevValues.client_id !== currentValues.client_id}
                    >
                        {({ getFieldValue }) => (
                            <Form.Item
                                label="Financial Year"
                                name="financialYear"
                                rules={[{ required: true, message: 'Financial Year is required' }]}
                            >
                                <Select onChange={handleFYChange}>
                                    {all_fys?.data?.map((fy) => (
                                        <Option value={fy._id} key={fy._id}>
                                            <div style={{ display: 'flex', gap: '1rem', fontWeight: 500 }}>
                                                <span>{moment(fy.start).format('YYYY')} - {moment(fy.end).format('YY')}</span>
                                                | <span> {fy.fy_type}</span>
                                                |<span> {fy.period_type}</span>
                                            </div>
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                    </Form.Item>

                    {!newFolder ? (
                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) =>
                                prevValues.financialYear !== currentValues.financialYear
                            }
                        >
                            {({ getFieldValue }) => (
                                <Form.Item
                                    label="Folder"
                                    name="folder_id"
                                    rules={[{ required: !newFolder, message: 'Please select a folder' }]}
                                >
                                    <Select
                                        dropdownRender={(menu) => (
                                            <>
                                                {menu}
                                                <Divider style={{ margin: '8px 0' }} />
                                                <button
                                                    className="m-2"
                                                    style={{ color: '#1677ff', border: 'none', background: 'transparent' }}
                                                    onClick={() => {
                                                        setNewFolder(true);
                                                        form.setFieldsValue({ folder_id: undefined });
                                                    }}
                                                >
                                                    + Create new folder
                                                </button>
                                            </>
                                        )}
                                    >
                                        {all_folders?.map((e) => (
                                            <Option value={e._id} key={e._id} label={e.name}>
                                                {e.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            )}
                        </Form.Item>
                    ) : (
                        <Form.Item
                            label="Create New Folder"
                            name="new_folder"
                            rules={[{ required: newFolder, message: 'Please enter a folder name' }]}
                        >
                            <Input
                                placeholder="Enter folder name"
                                suffix={
                                    <Button
                                        type="link"
                                        size="small"
                                        onClick={() => {
                                            setNewFolder(false);
                                            form.setFieldsValue({ new_folder: undefined });
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                }
                            />
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            {/* Preview Modal */}
            <Modal
                title={previewFile?.name}
                open={previewModalVisible}
                onCancel={() => {
                    setPreviewModalVisible(false);
                    setPreviewFile(null);
                }}
                width="80%"
                footer={[
                    <Button key="download" icon={<DownloadOutlined />}
                        onClick={() => handleDownload(previewFile)}>
                        Download
                    </Button>,
                    <Button key="close" onClick={() => {
                        setPreviewModalVisible(false);
                        setPreviewFile(null);
                    }}>
                        Close
                    </Button>
                ]}
                style={{ top: 20 }}
            >
                {renderPreviewContent(previewFile)}
            </Modal>
        </div>
    );
}

export default DrivePage;